/**
 * Sources (owner, 2026-10-15): every image a template was made from, kept and shown — the
 * uploaded references, the clear image, the AI's own magenta image and the stored print guide of
 * each Generate job behind it, and the template's own case image. Nothing here is ever deleted;
 * a click shows an image large.
 */
import { useState } from "react";
import type { GenerationJobView } from "@genz/platform";
import { Modal } from "../ui/Modal.js";

export interface SourceImage {
  label: string;
  url: string;
}

/** Every case reference of a job; one alone keeps the plain label. */
function caseSources(job: GenerationJobView): SourceImage[] {
  const urls = job.inputUrls?.length ? job.inputUrls : job.inputUrl ? [job.inputUrl] : [];
  return urls.map((url, i) => ({ label: urls.length > 1 ? `Case reference ${i + 1}` : "Case reference", url }));
}

/** A Generate job's stored images, in the order the job made them. */
export function jobSources(job: GenerationJobView): SourceImage[] {
  return [...caseSources(job), ...([

    ["Phone reference", job.productUrl],
    ["View reference", job.viewUrl],
    ["Clear image", job.clearUrl],
    ["AI magenta (original)", job.originalUrl],
    ["Print guide", job.resultUrl],
  ] as const).flatMap(([label, url]) => (url ? [{ label, url }] : []))];
}

export interface SourcesDialogProps {
  /** The Generate jobs behind the template, read fresh (their links expire). */
  jobs: GenerationJobView[];
  /** The template's own images: its saved case image, the shading fix's original. */
  templateImages: SourceImage[];
  loading: boolean;
  onClose: () => void;
}

function Tiles({ images, onPick }: { images: SourceImage[]; onPick: (image: SourceImage) => void }) {
  return (
    <ul className="sources__tiles">
      {images.map((image) => (
        <li key={image.url}>
          <button type="button" className="sources__tile" onClick={() => onPick(image)}
            title={`Show ${image.label} large`} data-testid="source-tile">
            <img src={image.url} alt={image.label} loading="lazy" />
            <span>{image.label}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function SourcesDialog({ jobs, templateImages, loading, onClose }: SourcesDialogProps) {
  const [shown, setShown] = useState<SourceImage | null>(null);
  return (
    <Modal title="Sources" subtitle="Every image this template was made from. Nothing here is deleted."
      wide onClose={onClose} testid="sources-dialog">
      {shown && (
        <figure className="sources__large" data-testid="source-large">
          <img src={shown.url} alt={shown.label} />
          <figcaption>
            {shown.label}{" "}
            <a href={shown.url} target="_blank" rel="noreferrer">Open full size</a>{" "}
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setShown(null)}>Back</button>
          </figcaption>
        </figure>
      )}
      {loading && <p className="faint" role="status">Reading the sources…</p>}
      {templateImages.length > 0 && (
        <section className="sources__group" data-testid="sources-template">
          <h3>This template</h3>
          <Tiles images={templateImages} onPick={setShown} />
        </section>
      )}
      {jobs.map((job) => (
        <section key={job.id} className="sources__group" data-testid="sources-job">
          <h3>Generate job {job.id.slice(0, 8)}{job.createdAt ? ` · ${new Date(job.createdAt).toLocaleString()}` : ""}</h3>
          <Tiles images={jobSources(job)} onPick={setShown} />
        </section>
      ))}
      {!loading && jobs.length === 0 && templateImages.length === 0 && (
        <p className="faint">No stored sources for this template.</p>
      )}
    </Modal>
  );
}
